import { useAppStore } from '@/lib/store';
import { formatTrx, formatUsd, formatNumber } from '@/lib/utils';
import {
  TrendingUp,
  DollarSign,
  Calendar,
  Target,
  Sparkles,
  Shield,
  Zap,
  Wifi,
} from 'lucide-react';

export default function SavingsPanel() {
  const { rows, trxPrice } = useAppStore();

  if (rows.length === 0) {
    return (
      <div className="card p-6 bg-white/5 backdrop-blur-sm border border-slate-700/50">
        <h2 className="text-lg font-semibold mb-4 text-white">
          Savings vs. Not Staking
        </h2>
        <div className="text-slate-400 text-center py-8">
          Configure your transfer settings to see how much you can save by
          staking TRX.
        </div>
      </div>
    );
  }

  const today = rows[0];
  const totalTrxNeeded = today.totalTrxNeeded;
  const dailyCostWithoutStaking = today.dailyCostWithoutStaking;

  const dailySavingsTrx = dailyCostWithoutStaking; // Daily cost you avoid by staking
  const dailySavingsUsd = dailySavingsTrx * trxPrice;
  const monthlySavingsUsd = dailySavingsUsd * 30;
  const yearlySavingsUsd = dailySavingsUsd * 365;

  // Calculate break-even point (how many days until staking pays for itself)
  const breakEvenDays =
    dailySavingsTrx > 0 && totalTrxNeeded > 0
      ? Math.ceil(totalTrxNeeded / dailySavingsTrx)
      : undefined;

  const savingsCards = [
    {
      title: 'Daily TRX Saved',
      value: formatTrx(dailySavingsTrx),
      subtitle: formatUsd(dailySavingsUsd),
      explanation: 'TRX you keep in your pocket every day by staking',
      icon: TrendingUp,
      color: 'text-green-400',
      bgColor: 'bg-green-500/10',
      borderColor: 'border-green-500/20',
    },
    {
      title: 'Monthly USD Saved',
      value: formatUsd(monthlySavingsUsd),
      subtitle: `${formatTrx(dailySavingsTrx * 30)} TRX`,
      explanation: 'Your monthly savings from avoiding daily burning',
      icon: DollarSign,
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/20',
    },
    {
      title: 'Yearly USD Saved',
      value: formatUsd(yearlySavingsUsd),
      subtitle: `${formatTrx(dailySavingsTrx * 365)} TRX`,
      explanation: 'Annual savings that compound over time',
      icon: Calendar,
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/20',
    },
    {
      title: 'Break-even Timeline',
      value:
        breakEvenDays && breakEvenDays !== Infinity
          ? `${breakEvenDays} days`
          : 'Immediate',
      subtitle:
        breakEvenDays && breakEvenDays !== Infinity
          ? `ROI achieved`
          : 'No waiting',
      explanation: 'How long until your staking investment pays for itself',
      icon: Target,
      color: 'text-orange-400',
      bgColor: 'bg-orange-500/10',
      borderColor: 'border-orange-500/20',
    },
  ];

  return (
    <div className="card p-6 bg-white/5 backdrop-blur-sm border border-slate-700/50">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-white">Your Staking Savings</h2>
          <p className="text-slate-400">
            See exactly how much you save by staking vs. paying daily fees
          </p>
        </div>
        <div className="p-2 bg-green-500/10 rounded-lg border border-green-500/20">
          <Sparkles className="h-5 w-5 text-green-400" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {savingsCards.map((card, index) => (
          <div
            key={index}
            className={`p-4 rounded-xl border ${card.borderColor} ${card.bgColor} backdrop-blur-sm`}
          >
            <div className={`p-2 rounded-lg ${card.bgColor} mb-3 inline-block`}>
              <card.icon className={`h-5 w-5 ${card.color}`} />
            </div>
            <h3 className="font-semibold text-sm text-slate-300 mb-2">
              {card.title}
            </h3>
            <p className="text-2xl font-bold text-white mb-2">{card.value}</p>
            <p className="text-sm text-slate-400 mb-2">{card.subtitle}</p>
            <p className="text-xs text-slate-500 leading-relaxed">
              {card.explanation}
            </p>
          </div>
        ))}
      </div>

      {/* Detailed Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Staking Scenario */}
        <div className="p-6 bg-green-500/10 rounded-xl border border-green-500/20">
          <div className="flex items-center space-x-2 mb-4">
            <Shield className="h-5 w-5 text-green-400" />
            <h3 className="font-semibold text-lg text-green-400">
              Staking Strategy
            </h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-slate-300">TRX to Stake:</span>
              <span className="font-semibold text-green-400">
                {formatTrx(totalTrxNeeded)}
              </span>
            </div>

            {/* Energy vs Bandwidth Breakdown */}
            <div className="p-3 bg-white/5 rounded-lg border border-white/10">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400 text-sm flex items-center space-x-2">
                    <Zap className="h-3 w-3 text-blue-400" />
                    Energy Staking:
                  </span>
                  <span className="font-medium text-blue-400 text-sm">
                    {formatTrx(today.trxNeeded)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400 text-sm flex items-center space-x-2">
                    <Wifi className="h-3 w-3 text-green-400" />
                    Bandwidth Staking:
                  </span>
                  <span className="font-medium text-green-400 text-sm">
                    {formatTrx(today.trxForBandwidth)}
                  </span>
                </div>
                <div className="border-t border-white/10 pt-2 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-300 text-sm font-medium">
                      Total:
                    </span>
                    <span className="font-semibold text-green-400 text-sm">
                      {formatTrx(today.totalTrxNeeded)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-slate-300">USD Investment:</span>
              <span className="font-semibold text-white">
                {formatUsd(totalTrxNeeded * trxPrice)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-300">Daily Cost:</span>
              <span className="font-semibold text-green-400">$0.00</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-300">Monthly Cost:</span>
              <span className="font-semibold text-green-400">$0.00</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-300">Yearly Cost:</span>
              <span className="font-semibold text-green-400">$0.00</span>
            </div>
          </div>
        </div>

        {/* Non-Staking Scenario */}
        <div className="p-6 bg-red-500/10 rounded-xl border border-red-500/20">
          <div className="flex items-center space-x-2 mb-4">
            <Zap className="h-5 w-5 text-red-400" />
            <h3 className="font-semibold text-lg text-red-400">
              Daily Fee Strategy
            </h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-slate-300">TRX Burned Daily:</span>
              <span className="font-semibold text-red-400">
                {formatTrx(dailyCostWithoutStaking)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-300">USD Value:</span>
              <span className="font-semibold text-white">
                {formatUsd(dailyCostWithoutStaking * trxPrice)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-300">Daily Cost:</span>
              <span className="font-semibold text-red-400">
                {formatUsd(dailyCostWithoutStaking * trxPrice)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-300">Monthly Cost:</span>
              <span className="font-semibold text-red-400">
                {formatUsd(dailyCostWithoutStaking * trxPrice * 30)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-300">Yearly Cost:</span>
              <span className="font-semibold text-red-400">
                {formatUsd(dailyCostWithoutStaking * trxPrice * 365)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="p-6 bg-slate-800/50 rounded-xl border border-slate-700/50">
        <h4 className="font-semibold mb-3 text-white">Your Savings Summary</h4>
        <p className="text-slate-300 leading-relaxed">
          By staking{' '}
          <span className="font-semibold text-green-400">
            {formatTrx(totalTrxNeeded)} TRX
          </span>{' '}
          instead of paying daily fees, you save{' '}
          <span className="font-semibold text-green-400">
            {formatUsd(dailySavingsUsd)} per day
          </span>
          ,{' '}
          <span className="font-semibold text-green-400">
            {formatUsd(monthlySavingsUsd)} per month
          </span>
          , and{' '}
          <span className="font-semibold text-green-400">
            {formatUsd(yearlySavingsUsd)} per year
          </span>
          .
          {breakEvenDays && breakEvenDays > 0 && breakEvenDays !== Infinity && (
            <>
              {' '}
              This investment pays for itself after approximately{' '}
              <span className="font-semibold text-orange-400">
                {breakEvenDays} days
              </span>
              .
            </>
          )}
          {(!breakEvenDays || breakEvenDays === Infinity) && (
            <>
              {' '}
              Since there are no daily costs with staking, this investment pays
              for itself immediately.
            </>
          )}
        </p>

        {/* Staking Breakdown Summary */}
        <div className="mt-4 p-4 bg-slate-700/50 rounded-lg border border-slate-600/50">
          <h5 className="font-semibold mb-3 text-white flex items-center space-x-2">
            <Shield className="h-4 w-4 text-blue-400" />
            <span>Staking Breakdown</span>
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-slate-400 flex items-center space-x-2">
                  <Zap className="h-3 w-3 text-blue-400" />
                  Energy Coverage:
                </span>
                <span className="font-medium text-blue-400">
                  {formatTrx(today.trxNeeded)} TRX
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400 flex items-center space-x-2">
                  <Wifi className="h-3 w-3 text-green-400" />
                  Bandwidth Coverage:
                </span>
                <span className="font-medium text-green-400">
                  {formatTrx(today.trxForBandwidth)} TRX
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Daily Energy:</span>
                <span className="text-slate-300">
                  {formatNumber(today.energyDay)} units
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Daily Bandwidth:</span>
                <span className="text-slate-300">
                  {formatNumber(today.bandwidthDay)} bytes
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
          <p className="text-sm text-green-200">
            <strong>🎯 Key Insight:</strong> Staking {formatTrx(totalTrxNeeded)}{' '}
            TRX gives you a daily renewable quota that eliminates all
            transaction fees. It's like buying a monthly pass instead of paying
            per ride!
          </p>
        </div>
      </div>
    </div>
  );
}
